import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import RedeemCodeDialog from "@/components/RedeemCodeDialog";
import { useAuth } from "@/context/AuthContext";
import { Ticket, CheckCircle2, PartyPopper } from "lucide-react";

export default function RedeemLanding() {
  const [params] = useSearchParams();
  const codeFromUrl = params.get("code") || "";
  const { user } = useAuth();
  const nav = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Auto-open dialog as soon as page mounts (only makes sense if the user is logged in)
  useEffect(() => {
    if (user && user.id) setDialogOpen(true);
  }, [user]);

  const afterRedeem = () => {
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-20 text-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Welcome to Soirée</div>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl sm:text-6xl mt-4 font-light tracking-tight">
          Let&apos;s unlock your invite.
        </h1>
        <p className="mt-5 text-neutral-600 leading-relaxed">
          Thank you for your Etsy purchase. Your redemption code is a one-time key that lets you publish one beautiful online invitation — with a live countdown, RSVP tracker, guestbook, and shareable link.
        </p>

        <div className="mt-10 border border-black/10 bg-white p-8 text-left">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center shrink-0">
              <Ticket size={18} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Step 1</div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-1 font-normal">
                {user && user.id ? "Paste your code" : "Create your free account"}
              </h3>
              <p className="text-sm text-neutral-600 mt-2">
                {user && user.id
                  ? "Your code looks like SOIREE-XXXX-XXXX. Paste it in the dialog to add a credit to your account."
                  : "Accounts are free — no payment details needed. Once you've signed up you can paste in your code."}
              </p>
              {user && user.id ? (
                <Button
                  onClick={() => setDialogOpen(true)}
                  data-testid="redeem-open-dialog"
                  className="mt-4 rounded-sm bg-neutral-900 hover:bg-neutral-800"
                >
                  Open redeem dialog
                </Button>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to={`/register${codeFromUrl ? `?next=${encodeURIComponent(`/redeem?code=${codeFromUrl}`)}` : ""}`}>
                    <Button data-testid="redeem-register" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">Create account</Button>
                  </Link>
                  <Link to={`/login${codeFromUrl ? `?next=${encodeURIComponent(`/redeem?code=${codeFromUrl}`)}` : ""}`}>
                    <Button variant="outline" data-testid="redeem-login" className="rounded-sm">I already have an account</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-black/10 my-6" />

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center shrink-0">
              <CheckCircle2 size={18} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Step 2</div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-1 font-normal">Design your invite</h3>
              <p className="text-sm text-neutral-600 mt-2">
                Pick an event type, a theme, a palette. Add your photos, a custom message and any extra RSVP questions. Preview as much as you like.
              </p>
            </div>
          </div>

          <div className="h-px bg-black/10 my-6" />

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center shrink-0">
              <PartyPopper size={18} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Step 3</div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-1 font-normal">Publish and share</h3>
              <p className="text-sm text-neutral-600 mt-2">
                Press Publish — we&apos;ll use your credit and generate a public link you can send to everyone you love.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-8 text-xs text-neutral-500">
          Lost your code?  <a href="mailto:hello@soiree-invites.com" className="underline underline-offset-4">hello@soiree-invites.com</a>
        </p>
      </div>
      <RedeemCodeDialog open={dialogOpen} onOpenChange={setDialogOpen} initialCode={codeFromUrl} onRedeemed={afterRedeem} />
    </div>
  );
}
